import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { sb } from '../supabase';
import { buildDisplayPrice, normalizeDisplayCurrency } from '../lib/displayCurrency';
import { getPrimaryProductImage } from '../lib/productMedia';
import { isSupplierDocsComplete, getSupplierMaabarId } from '../lib/supplierOnboarding';
import { fetchSupplierPublicProfileById } from '../lib/profileVisibility';
import SupplierMiniCard from '../components/supplier/SupplierMiniCard';
import { resolveCompanyName, ensureCompanyRomanization } from '../lib/companyRomanize';
import { buildSupplierBlurb } from '../lib/supplierBlurb';
import { PRODUCT_TIER_EMBED, deriveProductPriceFrom } from '../lib/productPriceLookup';
import { PRODUCT_CERT_EMBED, getProductCertTypes } from '../lib/productCertLookup';
import { T, getSpecialtyLabel } from '../lib/supplierDashboardConstants';
import { formatPriceLocale } from '../lib/formatLocale';
import { supplierEmailProductName } from '../lib/supplierEmailContent';
import ProductBuyerCardSummary from '../components/ProductBuyerCardSummary';
import BrandedLoading from '../components/BrandedLoading';
import TranslatedText from '../components/TranslatedText';
import { fetchSupplierReviews, reviewerLabel } from '../lib/supplierReviews';
import Footer from '../components/Footer';
import { keepsLegacyCatalog, useHioBuyOnly } from '../lib/marketMode';
import CertPills from '../components/CertPills';
import ProfileIcon from '../components/profile/ProfileIcon';

const SEND_EMAILS_URL = 'https://utzalmszfqfcofywfetv.supabase.co/functions/v1/send-email';
const SUPABASE_ANON_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InV0emFsbXN6ZnFmY29meXdmZXR2Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzM2NjE4NDAsImV4cCI6MjA4OTIzNzg0MH0.SSqFCeBRhKRIrS8oQasBkTsZxSv7uZGCT9pqfK-YmX8';

// Some legacy supplier rows have profiles.certifications stored as a JSON
// string instead of a jsonb array (double-encoded by an earlier upload
// path). Normalize both shapes so the buyer-facing card never sees a raw
// string. Returns [] for anything we can't make sense of.
function parseCerts(raw) {
  if (Array.isArray(raw)) return raw;
  if (typeof raw === 'string' && raw.trim()) {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
}

export default function SupplierProfile({ lang, user, profile, displayCurrency, exchangeRates }) {
  const { id } = useParams();
  const nav = useNavigate();
  const [supplier, setSupplier] = useState(null);
  // The three-line fallback for a supplier with no catalogue.
  const [miniCard, setMiniCard] = useState(null);
  const [allProducts, setProducts] = useState([]);
  // HioBuy-only mode: the legacy product grid is not shown to customers (the
  // profile itself stays — traders reach it from their conversations).
  // Suppliers and admins still see it. See lib/marketMode.js.
  const { hioBuyOnly } = useHioBuyOnly();
  const products = hioBuyOnly && !keepsLegacyCatalog(profile) ? [] : allProducts;
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sampleForms, setSampleForms] = useState({});
  const [sampleData, setSampleData] = useState({});
  const [sendingSample, setSendingSample] = useState({});
  const [calcQty, setCalcQty] = useState('');
  const [calcProduct, setCalcProduct] = useState(null);
  const [calcResult, setCalcResult] = useState(null);

  const isAr = lang === 'ar';
  const ar = isAr ? ' ar' : '';
  // Docs-complete = passed the 8-field verification path. Drives the badge only.
  const isReviewedSupplier = isSupplierDocsComplete(supplier?.status);
  const companyDescription = (isAr ? supplier?.bio_ar : lang === 'zh' ? supplier?.bio_zh : supplier?.bio_en) || supplier?.company_description || supplier?.bio_en || supplier?.bio_ar || supplier?.bio_zh || '';
  const supplierLanguages = Array.isArray(supplier?.languages) ? supplier.languages : [];
  const exportMarkets = Array.isArray(supplier?.export_markets) ? supplier.export_markets : [];
  const certifications = parseCerts(supplier?.certifications);

  useEffect(() => { loadSupplier(); }, [id, user?.id]);

  const loadSupplier = async () => {
    setLoading(true);

    let visibleSupplier = await fetchSupplierPublicProfileById(sb, id);

    if (!visibleSupplier && user?.id === id) {
      const { data: ownSupplier } = await sb
        .from('profiles')
        .select('*')
        .eq('id', id)
        .maybeSingle();
      visibleSupplier = ownSupplier || null;
    }

    // THE MINI-CARD FALLBACK. supplier_public_profiles requires EXISTS (an
    // active product), so a supplier who arrived by quoting through a public
    // link — products optional — is absent from it, and a TRADER clicking
    // through from their offer used to land on "المورد غير موجود". The
    // fallback above only covers a supplier viewing themselves.
    //
    // get_supplier_public_card reads profiles directly and returns three
    // fields: company + city, verified, category. Nothing else — no trade
    // link, no contact, no join date.
    if (!visibleSupplier) {
      const { data: cardRows, error: cardErr } = await sb.rpc('get_supplier_public_card', { p_supplier_id: id });
      if (cardErr) {
        console.error('[SupplierProfile] get_supplier_public_card failed:', cardErr.code || '', cardErr.message || cardErr);
      } else {
        const row = Array.isArray(cardRows) ? cardRows[0] : cardRows;
        if (row?.company_name) {
          setMiniCard(row);
          setSupplier(null);
          setProducts([]);
          setReviews([]);
          setLoading(false);
          return;
        }
      }
    }

    if (!visibleSupplier) {
      setSupplier(null);
      setProducts([]);
      setReviews([]);
      setLoading(false);
      return;
    }

    // (Removed) A former supplemental fetch read port_of_loading /
    // lead_time_min_days / lead_time_max_days / sample_available from
    // public.profiles. Those columns live on public.products (and
    // sample_available does not exist), never on profiles, so the query always
    // errored and was silently swallowed — it never populated anything. Dropped
    // as part of the RLS hardening to remove a dead base-table read. To surface
    // supplier-level trade fields, add the columns to profiles and to
    // supplier_public_profiles first.

    setSupplier(visibleSupplier);

    // Lazily romanize a Chinese company name (pinyin, not translation). The edge
    // task persists the result, so this fires at most once per supplier ever;
    // meanwhile the raw name shows and swaps to Latin when ready.
    ensureCompanyRomanization(visibleSupplier).then((latin) => {
      if (latin) setSupplier((prev) => (prev && prev.id === visibleSupplier.id ? { ...prev, company_name_latin: latin } : prev));
    });

    // Reviews through supplier_reviews_public — anonymous: no reviewer_id and
    // no request_id reach this page (owner, 2026-09-28).
    const [{ data: p }, r] = await Promise.all([
      sb.from('products').select(`*, ${PRODUCT_TIER_EMBED}, ${PRODUCT_CERT_EMBED}`).eq('supplier_id', id).eq('is_active', true),
      fetchSupplierReviews(sb, id),
    ]);

    setProducts(p || []);
    setReviews(r || []);
    setLoading(false);
  };

  const toggleSampleForm = (productId) => {
    setSampleForms(prev => ({ ...prev, [productId]: !prev[productId] }));
    setSampleData(prev => ({ ...prev, [productId]: prev[productId] || { qty: '1', note: '' } }));
  };

  const submitSample = async (p) => {
    if (!user) { nav('/login/buyer'); return; }
    const d = sampleData[p.id] || { qty: '1', note: '' };
    const maxQty = p.sample_max_qty || 3;
    if (parseInt(d.qty) > maxQty) {
      alert(isAr ? `الحد الأقصى ${maxQty} قطع` : `Max ${maxQty} units`);
      return;
    }
    setSendingSample(prev => ({ ...prev, [p.id]: true }));
    const total = (parseFloat(p.sample_price || 0) + parseFloat(p.sample_shipping || 0)) * parseInt(d.qty);
    const { error } = await sb.from('samples').insert({
      product_id: p.id,
      supplier_id: id,
      buyer_id: user.id,
      quantity: parseInt(d.qty),
      sample_price: parseFloat(p.sample_price || 0),
      shipping_price: parseFloat(p.sample_shipping || 0),
      total_price: total,
      notes: d.note || '',
      status: 'pending',
    });
    setSendingSample(prev => ({ ...prev, [p.id]: false }));
    if (error) { alert(isAr ? 'حدث خطأ' : 'Error'); return; }
    await sb.from('notifications').insert({
      user_id: id, type: 'new_sample',
      title_ar: 'طلب عينة جديد على منتجك',
      title_en: 'New sample request on your product',
      title_zh: '您的产品收到了新样品请求',
      ref_id: p.id, is_read: false
    });
    try {
      await fetch(SEND_EMAILS_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${SUPABASE_ANON_KEY}` },
        body: JSON.stringify({
          type: 'new_sample',
          data: {
            recipientUserId: id,
            // English first: this mail goes to the SUPPLIER whose product it
            // is, not to the trader browsing the page.
            productName: supplierEmailProductName(p, 'Product'),
            quantity: d.qty,
            totalPrice: total,
            // ⚠️ `lang` REMOVED — it was the TRADER's UI language, so browsing
            // in Arabic sent the Chinese supplier an Arabic email. new_sample
            // reads `d.lang || 'en'`, so omitting it is English.
            // Note `supplier.lang` IS on this page (the profile is fetched
            // with select('*')), but using it here and 'en' everywhere else
            // would make one of four supplier mails behave differently for no
            // stated reason. One rule, four sites — changing all four to the
            // supplier's own language is the follow-up.
          },
        }),
      });
    } catch (e) { console.error('sample email error:', e); }
    alert(isAr ? '✅ تم إرسال طلب العينة!' : '✅ Sample request sent!');
    toggleSampleForm(p.id);
  };

  const fmt = (n) => Number(n).toLocaleString('ar-SA-u-nu-latn', { maximumFractionDigits: 2 });

  const calcPrice = () => {
    if (!calcProduct || !calcQty) return;
    const qty = parseFloat(calcQty);
    // In the viewer's display currency — SAR for every trader and visitor
    // (owner, 2026-09-29), at the admin rate; never the product's own USD.
    const conv = (n) => buildDisplayPrice({ amount: n, sourceCurrency: calcProduct.currency || 'USD', displayCurrency: displayCurrency || 'SAR', rates: exchangeRates, lang }).displayAmount;
    const price = conv(deriveProductPriceFrom(calcProduct));
    setCalcResult({
      unitPrice: Math.round(price * 100) / 100,
      total: Math.round(qty * price * 100) / 100,
      currency: normalizeDisplayCurrency(displayCurrency || 'SAR'),
      meetsmoq: qty >= parseFloat(calcProduct.moq || 1),
      moq: calcProduct.moq,
    });
  };

  if (loading) return (
    <div className="profile-wrap">
      <BrandedLoading lang={lang} tone="supplier" />
    </div>
  );

  // A supplier with no catalogue: the mini-card instead of a dead end. Three
  // lines, and the verified line only when we have actually checked.
  if (!supplier && miniCard) return (
    <div className="profile-wrap">
      <div className="profile-body">
        <SupplierMiniCard card={miniCard} lang={lang} />
      </div>
    </div>
  );

  if (!supplier) return (
    <div className="profile-wrap">
      <div className="profile-body">
        <p style={{ color: '#7a7a7a' }}>{isAr ? 'المورد غير موجود' : 'Supplier not found'}</p>
      </div>
    </div>
  );

  // Phase 5D — aggregate cert types across all of the supplier's products.
  // Computed once per render (cheap; products is already in memory).
  const tT = T[lang] || T.en;
  // When there's no real bio, show a templated blurb built only from fields we
  // actually have (sector/location/verified) — never a blank About section.
  const supplierBlurb = companyDescription ? '' : buildSupplierBlurb(supplier, lang);
  const supplierAggregateCertTypes = (() => {
    const seen = new Set();
    const ordered = [];
    for (const p of (products || [])) {
      for (const code of getProductCertTypes(p)) {
        if (!seen.has(code)) { seen.add(code); ordered.push(code); }
      }
    }
    return ordered;
  })();

  // Phase 6A — currency-aware min-order text (stored value treated as SAR
  // per supplier dashboard label convention).
  const minOrderText = (() => {
    if (!supplier.min_order_value) return null;
    const built = buildDisplayPrice({
      amount: supplier.min_order_value,
      sourceCurrency: 'SAR',
      displayCurrency: displayCurrency || 'SAR',
      rates: exchangeRates,
      lang,
    });
    return `${formatPriceLocale(built.displayAmount, lang, { fractionDigits: 0 })} ${built.displayCurrency}`;
  })();

  // Phase 6B — factory_images normalization.
  // The column historically holds two formats:
  //   (a) full public URLs to product-images bucket  → buyer-renderable
  //   (b) raw path strings stored in supplier-docs   → private; buyer cannot read
  // Filter to (a) only so we never render a broken <img> over the cream wrapper.
  const renderableFactoryImages = (Array.isArray(supplier.factory_images) ? supplier.factory_images : [])
    .filter((img) => typeof img === 'string' && /^https?:\/\//i.test(img) && !img.includes('/supplier-docs/'));

  const yearsExp = Number(supplier.years_experience);
  const yrsSuffix = isAr ? 'سنة' : lang === 'zh' ? '年' : 'yrs';

  // Phase 7 — Trade-info expansion:
  // min_order_value moved here from the stats row, plus port/lead-time/
  // sample_available pulled from the supplemental fetch above. lead-time
  // collapses min/max bounds into one "X-Y days" string.
  let leadTimeText = '';
  const lmin = supplier.lead_time_min_days;
  const lmax = supplier.lead_time_max_days;
  if (Number.isFinite(lmin) && Number.isFinite(lmax)) leadTimeText = `${lmin}-${lmax}`;
  else if (Number.isFinite(lmin)) leadTimeText = String(lmin);
  else if (Number.isFinite(lmax)) leadTimeText = String(lmax);
  if (leadTimeText) {
    const dayLabel = isAr ? 'يوم' : lang === 'zh' ? '天' : 'days';
    leadTimeText = `${leadTimeText} ${dayLabel}`;
  }

  // Fact cards in the factory profile's About grid. Location and address are
  // in the hero, as on the factory page, so they are not repeated here.
  const detailItems = [
    supplier.business_type ? { icon: 'box', label: isAr ? 'نوع النشاط' : lang === 'zh' ? '企业类型' : 'Business type', value: supplier.business_type, translatable: true } : null,
    supplier.year_established ? { icon: 'clock', label: isAr ? 'سنة التأسيس' : lang === 'zh' ? '成立年份' : 'Established', value: supplier.year_established } : null,
    Number.isFinite(yearsExp) && yearsExp > 0 ? { icon: 'star', label: isAr ? 'الخبرة' : lang === 'zh' ? '经验' : 'Experience', value: `${yearsExp} ${yrsSuffix}` } : null,
    supplier.num_employees ? { icon: 'users', label: isAr ? 'عدد الموظفين' : lang === 'zh' ? '员工人数' : 'Employees', value: supplier.num_employees } : null,
    exportMarkets.length > 0 ? { icon: 'globe', label: isAr ? 'أسواق التصدير' : lang === 'zh' ? '出口市场' : 'Export markets', value: exportMarkets.join(' · ') } : null,
    supplier.export_years ? { icon: 'truck', label: isAr ? 'سنوات التصدير' : lang === 'zh' ? '出口年限' : 'Export years', value: `${supplier.export_years} ${yrsSuffix}` } : null,
    minOrderText ? { icon: 'box', label: isAr ? 'الحد الأدنى للطلب' : lang === 'zh' ? '最小起订量' : 'Min. Order', value: minOrderText } : null,
    supplier.customization_support ? { icon: 'tag', label: isAr ? 'التخصيص' : lang === 'zh' ? '定制' : 'Customization', value: supplier.customization_support, translatable: true } : null,
    // (Removed 2026-09-21) The "Port of Loading" row. `port_of_loading` exists
    // on public.profiles but is written by NOTHING (0 rows) and is not a column
    // on supplier_public_profiles, which is where `supplier` comes from — so
    // this test read `undefined` on every render and the row could never
    // appear. The real per-product port lives on public.products and is edited
    // in ProductComposer.
    leadTimeText ? { icon: 'clock', label: isAr ? 'مدة التصنيع' : lang === 'zh' ? '生产交期' : 'Lead Time', value: leadTimeText } : null,
    supplier.sample_available === true ? { icon: 'check', label: isAr ? 'العينات' : lang === 'zh' ? '样品' : 'Samples', value: isAr ? 'متاح ✓' : lang === 'zh' ? '可提供 ✓' : 'Available ✓' } : null,
    supplierLanguages.length > 0 ? { icon: 'globe', label: isAr ? 'اللغات' : lang === 'zh' ? '支持语言' : 'Languages', value: supplierLanguages.join(' · ') } : null,
  ].filter(Boolean);

  const company = resolveCompanyName(supplier);
  const specialtyLabel = supplier.speciality && supplier.speciality !== 'other' ? getSpecialtyLabel(supplier.speciality, lang) : '';
  const location = [supplier.city, supplier.country].filter(Boolean).join(isAr ? '، ' : ', ');
  const maabarId = isReviewedSupplier ? getSupplierMaabarId(supplier) : '';
  const heroImage = supplier.cover_photo_url || supplier.avatar_url || null;
  const hasSamples = products.some(p => p.sample_available);
  const openChat = () => { if (!user) nav('/login'); else nav(`/chat/${supplier.id}`); };

  // Same layout as FactoryDetail (the fp-* classes in index.css) so a trader
  // sees one profile design whichever source the company came from. Badges
  // are claims, so each maps to one stored signal: "موثّق" = admin approved
  // the documents. There is no "field verified" badge — no field visit is
  // recorded anywhere, and the old one was shown to every supplier.
  return (
    <div className="full-page" dir={isAr ? 'rtl' : 'ltr'}>
      <div className="fx-wrap">
        <div className={`fp-crumb${ar}`}>
          <button type="button" onClick={() => nav('/')}>{isAr ? 'الرئيسية' : lang === 'zh' ? '首页' : 'Home'}</button><span className="sep">›</span>
          <button type="button" onClick={() => nav('/factories')}>{isAr ? 'المصانع' : lang === 'zh' ? '工厂' : 'Factories'}</button>
          <span className="sep">›</span><span className="cur">{company}</span>
        </div>

        {/* ── Hero ── */}
        <div className="fp-hero">
          <div className="fp-cover">
            {heroImage ? (
              <div className="fp-cover-single">
                <img src={heroImage} alt={company} style={supplier.cover_photo_url ? undefined : { objectFit: 'contain' }} />
              </div>
            ) : (
              <div className="fp-cover-initial">{(company || '?')[0]}</div>
            )}
          </div>

          <div className="fp-info">
            <div className="fp-name-row">
              <h1 className={`fp-name${ar}`}>{company}</h1>
            </div>
            {isReviewedSupplier && (
              <div className="fp-badges">
                <span className={`fp-badge verified${ar}`}><ProfileIcon name="check" size={12} />{isAr ? 'موثّق' : lang === 'zh' ? '已认证' : 'Verified'}</span>
              </div>
            )}
            {(specialtyLabel || location) && (
              <p className={`fp-cat-line${ar}`}>
                {specialtyLabel && <span>{specialtyLabel}</span>}
                {location && <span className="loc">{location}</span>}
              </p>
            )}
            {supplier.company_address && (
              <p className={`fp-addr${ar}`} style={{ display: 'flex', alignItems: 'flex-start', gap: 6, margin: '6px 0 0',
                fontSize: 13, color: 'var(--text-secondary)', fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
                <span style={{ color: '#8B7355', flexShrink: 0, marginTop: 2 }}><ProfileIcon name="pin" size={13} /></span>
                <span>{supplier.company_address}</span>
              </p>
            )}
            {maabarId && <p className="fp-mid">Maabar ID · {maabarId}</p>}

            {/* Description — the supplier's own bio (AI-translated), else a
                blurb templated from fields we actually have. */}
            {(companyDescription || supplierBlurb) && (
              <div className={`fp-desc${ar}`}>
                {companyDescription ? <TranslatedText text={companyDescription} lang={lang} /> : supplierBlurb}
              </div>
            )}

            {/* Cert types carried by this supplier's products (product-level). */}
            {supplierAggregateCertTypes.length > 0 && (
              <CertPills certs={supplierAggregateCertTypes} lang={lang} size="lg" style={{ margin: '0 0 18px' }} />
            )}

            <button className={`fp-cta${ar}`} onClick={openChat}>
              <ProfileIcon name="headset" size={18} />{isAr ? 'راسل المورد مباشرة' : lang === 'zh' ? '直接联系供应商' : 'Message the supplier'}
            </button>
            {hasSamples && (
              <button type="button" className={`fp-cta-2${ar}`}
                onClick={() => document.getElementById('sp-products')?.scrollIntoView({ behavior: 'smooth' })}>
                {isAr ? 'طلب عينة' : lang === 'zh' ? '请求样品' : 'Request Sample'}
              </button>
            )}
          </div>
        </div>

        {/* ── Quality certifications — the supplier's own uploaded files ── */}
        {certifications.length > 0 && (
          <div className="fp-sec">
            <div className="fp-sec-head">
              <h2 className={`fp-sec-title${ar}`}>{isAr ? 'شهادات الجودة' : lang === 'zh' ? '质量认证' : 'Quality Certifications'}</h2>
            </div>
            <div className="fp-certs">
              {certifications.map((cert, i) => (
                <div className="fp-cert" key={i}>
                  <p className={`fp-cert-name${ar}`}><ProfileIcon name="award" size={17} />{cert.name}</p>
                  {cert.issuer && <p className={`fp-cert-meta${ar}`}>{isAr ? 'الجهة المانحة' : lang === 'zh' ? '颁发机构' : 'Issued by'} · {cert.issuer}</p>}
                  {cert.valid_until && <p className={`fp-cert-meta${ar}`}>{isAr ? 'صالحة حتى' : lang === 'zh' ? '有效期至' : 'Valid until'} · {cert.valid_until}</p>}
                  {cert.file_url && (
                    <a className={`fp-cert-link${ar}`} href={cert.file_url} target="_blank" rel="noreferrer">{tT.spViewCertificate}</a>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Factory photos (public URLs only — see renderableFactoryImages) ── */}
        {renderableFactoryImages.length > 0 && (
          <div className="fp-sec">
            <div className="fp-sec-head">
              <h2 className={`fp-sec-title${ar}`}>{tT.spFactoryPhotosLabel}</h2>
            </div>
            <div className="fp-photos">
              {renderableFactoryImages.slice(0, 8).map((img, index) => (
                <a key={`${img}-${index}`} className="fp-photo" href={img} target="_blank" rel="noreferrer">
                  <img
                    src={img}
                    alt=""
                    loading="lazy"
                    onError={(e) => {
                      // A URL that passed the filter can still 404 (deleted
                      // file) — hide the cell rather than show an empty box.
                      const cell = e.currentTarget.parentElement;
                      if (cell) cell.style.display = 'none';
                    }}
                  />
                </a>
              ))}
            </div>
          </div>
        )}

        {/* ── About — fact cards, only for fields the supplier filled in ── */}
        {detailItems.length > 0 && (
          <div className="fp-sec">
            <div className="fp-sec-head">
              <h2 className={`fp-sec-title${ar}`}>{isAr ? 'نبذة عن الشركة' : lang === 'zh' ? '公司简介' : 'About the company'}</h2>
            </div>
            <div className="fp-about-grid" style={{ marginTop: 0 }}>
              {detailItems.map(item => (
                <div className="fp-about-card" key={item.label}>
                  <ProfileIcon name={item.icon} size={20} />
                  <div>
                    <p className={`fp-about-t${ar}`}>{item.label}</p>
                    {item.translatable
                      ? <div className={`fp-about-v${ar}`}><TranslatedText text={String(item.value)} lang={lang} /></div>
                      : <p className={`fp-about-v${ar}`}>{item.value}</p>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Products (hidden entirely when the supplier has none — matches certs/factory/reviews) ── */}
        {products.length > 0 && (
        <div id="sp-products" className="fp-sec">
          <div className="fp-sec-head">
            <h2 className={`fp-sec-title${ar}`}>{isAr ? 'المنتجات' : lang === 'zh' ? '产品' : 'Products'} ({products.length})</h2>
          </div>
            <div className="product-grid">
              {products.map((p, idx) => {
                const productPriceFrom = deriveProductPriceFrom(p);
                const price = buildDisplayPrice({ amount: productPriceFrom, sourceCurrency: p.currency || 'USD', displayCurrency: displayCurrency || p.currency || 'USD', rates: exchangeRates, lang });
                const animation = { animation: `fadeIn 0.4s ease ${idx * 0.04}s both` };
                return (
                  <div key={p.id}>
                    <div className="product-card" style={animation} onClick={() => nav(`/products/${p.id}`)}>
                      <div className="product-card-img">
                        {getPrimaryProductImage(p)
                          ? <img src={getPrimaryProductImage(p)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#FAF8F5' }}>
                              <span style={{ fontSize: 30, fontWeight: 300, color: '#8B7355', fontFamily: "'Cormorant Garamond', serif" }}>
                                {((isAr ? p.name_ar || p.name_en : lang === 'zh' ? p.name_zh || p.name_en : p.name_en || p.name_ar) || '?').trim()[0] || '?'}
                              </span>
                            </div>}
                      </div>
                      <div className="product-card-body">
                        <h3 className={`product-card-name${isAr ? ' ar' : ''}`}>
                          {isAr ? p.name_ar || p.name_en : lang === 'zh' ? p.name_zh || p.name_en : p.name_en || p.name_ar}
                        </h3>
                        <ProductBuyerCardSummary
                          product={p}
                          displayCurrency={displayCurrency || p.currency || 'USD'}
                          exchangeRates={exchangeRates}
                          lang={lang}
                        />
                        <button className="product-card-buy" onClick={e => { e.stopPropagation(); nav(`/products/${p.id}`); }}>
                          {tT.pdBuyNowBtn}
                        </button>
                        {p.sample_available && (
                          <button
                            onClick={e => { e.stopPropagation(); toggleSampleForm(p.id); }}
                            style={{ background: 'none', border: 'none', padding: 0, fontSize: 11, color: '#2d7a4f', cursor: 'pointer', fontFamily: "'Tajawal', sans-serif", textAlign: isAr ? 'right' : 'left', marginTop: 2 }}>
                            {isAr ? `طلب عينة — ${fmt(p.sample_price)} ر.س` : `Sample — ${fmt(p.sample_price)} SAR`}
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Sample form */}
                    {sampleForms[p.id] && (
                      <div style={{
                        background: 'rgba(45,122,79,0.04)', border: '1px solid rgba(45,122,79,0.2)',
                        borderTop: 'none', padding: '20px 16px', marginBottom: 8,
                        borderRadius: '0 0 8px 8px', animation: 'fadeIn 0.3s ease',
                      }}>
                        <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 16, fontFamily: isAr ? 'var(--font-ar)' : 'inherit' }}>
                          {isAr
                            ? `سعر الوحدة: ${fmt(p.sample_price)} ريال + شحن: ${fmt(p.sample_shipping || 0)} ريال · الحد الأقصى: ${p.sample_max_qty || 3} قطع`
                            : `Unit: ${fmt(p.sample_price)} SAR + Ship: ${fmt(p.sample_shipping || 0)} SAR · Max: ${p.sample_max_qty || 3}`}
                        </p>
                        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 12 }}>
                          <div style={{ flex: '0 0 110px' }}>
                            <label style={{ display: 'block', fontSize: 10, letterSpacing: 1.5, textTransform: 'uppercase', color: '#7a7a7a', marginBottom: 6 }}>
                              {isAr ? 'الكمية' : 'Quantity'}
                            </label>
                            <input
                              style={{ width: '100%', padding: '10px 12px', border: '1px solid var(--border-default)', background: 'var(--bg-raised)', fontSize: 14, color: 'var(--text-primary)', outline: 'none', borderRadius: 3 }}
                              type="number" min="1" max={p.sample_max_qty || 3}
                              value={sampleData[p.id]?.qty || '1'}
                              onChange={e => setSampleData(prev => ({ ...prev, [p.id]: { ...prev[p.id], qty: e.target.value } }))}
                            />
                          </div>
                          <div style={{ background: 'var(--bg-hover)', padding: '10px 14px', borderRadius: 3, minWidth: 110 }}>
                            <p style={{ fontSize: 10, color: 'var(--text-secondary)', marginBottom: 4, letterSpacing: 1 }}>{isAr ? 'الإجمالي' : 'TOTAL'}</p>
                            <p style={{ fontSize: 18, fontWeight: 300, color: 'var(--text-primary)', fontFamily: 'var(--font-en)' }}>
                              {fmt((parseFloat(p.sample_price || 0) + parseFloat(p.sample_shipping || 0)) * parseInt(sampleData[p.id]?.qty || 1))} SAR
                            </p>
                          </div>
                        </div>
                        <div style={{ marginBottom: 12 }}>
                          <label style={{ display: 'block', fontSize: 10, letterSpacing: 1.5, textTransform: 'uppercase', color: '#7a7a7a', marginBottom: 6 }}>
                            {isAr ? 'ملاحظة' : 'Note'}
                          </label>
                          <input
                            style={{ width: '100%', padding: '10px 12px', border: '1px solid var(--border-default)', background: 'var(--bg-raised)', fontSize: 13, color: 'var(--text-primary)', outline: 'none', borderRadius: 3, boxSizing: 'border-box' }}
                            value={sampleData[p.id]?.note || ''}
                            onChange={e => setSampleData(prev => ({ ...prev, [p.id]: { ...prev[p.id], note: e.target.value } }))}
                            placeholder={isAr ? 'اللون، المواصفات...' : 'Color, specs...'}
                          />
                        </div>
                        {p.sample_note && (
                          <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 12, padding: '8px 12px', background: 'var(--bg-hover)', borderRadius: 3, fontFamily: isAr ? 'var(--font-ar)' : 'inherit' }}>
                            {p.sample_note}
                          </p>
                        )}
                        {!user && (
                          <p style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 12 }}>
                            {isAr ? 'سيُطلب منك تسجيل الدخول عند الإرسال' : "You'll be asked to sign in when submitting"}
                          </p>
                        )}
                        <div style={{ display: 'flex', gap: 10 }}>
                          <button
                            style={{ background: '#2d7a4f', color: '#fff', border: 'none', padding: '10px 18px', fontSize: 12, cursor: 'pointer', borderRadius: 3, opacity: sendingSample[p.id] ? 0.5 : 1 }}
                            onClick={() => submitSample(p)} disabled={sendingSample[p.id]}>
                            {sendingSample[p.id] ? '...' : isAr ? 'إرسال طلب العينة' : 'Send Sample Request'}
                          </button>
                          <button
                            onClick={() => toggleSampleForm(p.id)}
                            style={{ background: 'none', border: '1px solid #e8e5de', color: '#6b6560', padding: '10px 14px', fontSize: 12, cursor: 'pointer', borderRadius: 3 }}>
                            {isAr ? 'إلغاء' : 'Cancel'}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
        </div>
        )}

        {/* ── Price calculator ── */}
        {products.length > 0 && (
          <div className="fp-sec">
            <div className="fp-sec-head">
              <h2 className={`fp-sec-title${ar}`}>{isAr ? 'احسب سعرك' : lang === 'zh' ? '价格计算' : 'Price Calculator'}</h2>
            </div>
            <div className="fp-about">
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
              <select
                style={{ flex: 1, minWidth: 140, padding: '10px 12px', fontSize: 13, border: '1px solid #e8e5de', background: '#faf9f7', color: '#6b6560', borderRadius: 8, outline: 'none', cursor: 'pointer', minHeight: 42, fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}
                value={calcProduct?.id || ''}
                onChange={e => { setCalcProduct(products.find(p => p.id === e.target.value) || null); setCalcResult(null); }}>
                <option value="">{isAr ? 'اختر منتج' : 'Select product'}</option>
                {products.map(p => (
                  <option key={p.id} value={p.id}>
                    {isAr ? p.name_ar || p.name_en : p.name_en || p.name_ar}
                  </option>
                ))}
              </select>
              <input
                style={{ width: 110, padding: '10px 12px', fontSize: 13, border: '1px solid #e8e5de', background: '#faf9f7', color: '#1a1814', borderRadius: 8, outline: 'none', minHeight: 42 }}
                type="number"
                placeholder={isAr ? 'الكمية' : 'Quantity'}
                value={calcQty}
                onChange={e => { setCalcQty(e.target.value); setCalcResult(null); }}
              />
              <button
                onClick={calcPrice}
                style={{ background: '#1a1814', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 18px', fontSize: 13, fontFamily: "'Tajawal', sans-serif", fontWeight: 600, cursor: 'pointer', minHeight: 42 }}>
                {isAr ? 'احسب' : 'Calculate'}
              </button>
            </div>
            {calcResult && (
              <div style={{ borderTop: '1px solid #e8e5de', paddingTop: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                  <span style={{ fontSize: 12, color: '#b0ab9e' }}>{isAr ? 'سعر الوحدة' : 'Unit Price'}</span>
                  <span style={{ fontSize: 12, color: '#1a1814', fontFamily: "'Cormorant Garamond', serif", fontVariantNumeric: 'lining-nums' }}>{calcResult.unitPrice} {calcResult.currency}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: calcResult.meetsmoq ? 0 : 10 }}>
                  <span style={{ fontSize: 14, fontWeight: 600, color: '#1a1814', fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>{isAr ? 'الإجمالي' : 'Total'}</span>
                  <span style={{ fontSize: 20, fontWeight: 300, color: '#1a1814', fontFamily: "'Cormorant Garamond', serif", fontVariantNumeric: 'lining-nums' }}>{calcResult.total.toLocaleString()} {calcResult.currency}</span>
                </div>
                {!calcResult.meetsmoq && (
                  <p style={{ fontSize: 11, color: '#a08850', padding: '6px 10px', background: 'rgba(122,96,48,0.1)', borderRadius: 8, border: '1px solid rgba(122,96,48,0.2)', marginTop: 8, fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
                    {isAr ? `الحد الأدنى للطلب: ${calcResult.moq} قطعة` : `Min order: ${calcResult.moq} units`}
                  </p>
                )}
              </div>
            )}
            </div>
          </div>
        )}

        {/* ── Reviews ── */}
        {reviews.length > 0 && (
          <div className="fp-sec">
            <div className="fp-sec-head">
              <h2 className={`fp-sec-title${ar}`}>{isAr ? 'التقييمات' : lang === 'zh' ? '评价' : 'Reviews'}</h2>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {reviews.map((rv, i) => (
                <div key={rv.id || i} style={{ padding: '14px 16px', background: '#fff', border: '1px solid #e8e5de', borderRadius: 10 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                    <span style={{ fontSize: 12, color: '#6b6560', fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
                      {reviewerLabel(lang)}
                    </span>
                    <span style={{ color: '#f5a623', fontSize: 13 }}>
                      {Array.from({ length: 5 }, (_, j) => j < (rv.rating || 0) ? '★' : '☆').join('')}
                    </span>
                  </div>
                  {rv.comment && (
                    <p style={{ fontSize: 13, color: '#6b6560', lineHeight: 1.6, margin: 0, fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
                      {rv.comment}
                    </p>
                  )}
                  {(rv.quality_rating || rv.shipping_rating || rv.communication_rating) && (
                    <div style={{ display: 'flex', gap: 12, marginTop: 8, flexWrap: 'wrap' }}>
                      {rv.quality_rating && <span style={{ fontSize: 10, color: '#b0ab9e' }}>{isAr ? 'الجودة' : 'Quality'}: {rv.quality_rating}/5</span>}
                      {rv.shipping_rating && <span style={{ fontSize: 10, color: '#b0ab9e' }}>{isAr ? 'الشحن' : 'Shipping'}: {rv.shipping_rating}/5</span>}
                      {rv.communication_rating && <span style={{ fontSize: 10, color: '#b0ab9e' }}>{isAr ? 'التواصل' : 'Comm'}: {rv.communication_rating}/5</span>}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Bottom CTA band — same as the factory profile ── */}
        <div className="fp-band">
          <div className="fp-band-main">
            <h3 className={`fp-band-t${ar}`}>{isAr ? 'هل ترغب بالتواصل مع هذا المورد؟' : lang === 'zh' ? '想联系这家供应商吗？' : 'Want to talk to this supplier?'}</h3>
            <p className={`fp-band-s${ar}`}>{isAr ? 'راسل المورد مباشرة عبر معبر، والمحادثة تُترجم تلقائيًا.' : lang === 'zh' ? '通过 Maabar 直接联系供应商，对话自动翻译。' : 'Message the supplier directly through Maabar — the conversation is translated automatically.'}</p>
          </div>
          <button className={`fp-band-btn${ar}`} onClick={openChat}>{isAr ? 'راسل المورد' : lang === 'zh' ? '联系供应商' : 'Message the supplier'}<ProfileIcon name="send" size={17} /></button>
        </div>
      </div>

      <Footer lang={lang} />
    </div>
  );
}
